<template>
  <Teleport to="body">
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h3>Cell Background Color</h3>
        <div class="modal-body">
          <div class="color-picker-container">
            <input type="color" v-model="cellBgColor" class="color-picker" />
            <input
              type="text"
              v-model="cellBgColor"
              placeholder="#RRGGBB"
              class="color-input"
            />
          </div>
          <div class="color-presets">
            <button
              v-for="color in colorPresets"
              :key="color"
              @click="cellBgColor = color"
              :style="{ backgroundColor: color }"
              class="color-preset-btn"
            ></button>
            <button
              @click="cellBgColor = null"
              class="color-preset-btn clear-color"
              title="Clear Color"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">Cancel</button>
          <button @click="applyTableCellBgColor">Apply</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  showModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const cellBgColor = ref("#f0f0f0");
const colorPresets = [
  "#ffffff",
  "#f8f9fa",
  "#e9ecef",
  "#dee2e6",
  "#f8f8d7",
  "#f3f0ff",
  "#e6f4ff",
  "#e6fffb",
];

const closeModal = () => {
  emit("close");
};

const applyTableCellBgColor = () => {
  const color =
    cellBgColor.value === "#ffffff" || !cellBgColor.value
      ? null
      : cellBgColor.value;
  props.editor
    .chain()
    .focus()
    .updateAttributes("tableCell", { background: color })
    .run();
  props.editor
    .chain()
    .focus()
    .updateAttributes("tableHeader", { background: color })
    .run();
  closeModal();
};
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 500px;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.25em;
  color: #343a40;
  text-align: center;
}

.modal-body {
  margin-bottom: 20px;
}

/* Color picker styles */
.color-picker-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.color-picker {
  height: 40px;
  width: 60px;
  padding: 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.color-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.color-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.color-preset-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
}

.clear-color {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: #495057;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-footer button {
  padding: 8px 15px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  cursor: pointer;
  font-size: 0.9em;
}

.modal-footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer button.btn-cancel {
  background-color: #f8f9fa;
  color: #495057;
}
.modal-footer button.btn-cancel:hover {
  background-color: #e9ecef;
}

.modal-footer button:not(.btn-cancel) {
  background-color: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
}
.modal-footer button:not(.btn-cancel):not(:disabled):hover {
  background-color: var(--primary-blue-dark);
  border-color: var(--primary-blue-dark);
}
</style>
